import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarDays, Download, Pencil, Save, X } from "lucide-react";
import { downloadElementAsPdf } from "@/lib/exportPdf";

const MONTHS = ["JANUARI", "FEBRUARI", "MAC", "APRIL", "MEI", "JUN", "JULAI", "OGOS", "SEPTEMBER", "OKTOBER", "NOVEMBER", "DISEMBER"];
const DOW = ["Ahad", "Isnin", "Selasa", "Rabu", "Khamis", "Jumaat", "Sabtu"];
const COLORS = [
  { key: "none", label: "Tiada", hex: "#ffffff" },
  { key: "green", label: "Balik", hex: "#BBF7D0" },
  { key: "blue", label: "Outing", hex: "#BAE6FD" },
  { key: "gold", label: "Cuti Penggal", hex: "#FDE68A" },
  { key: "orange", label: "Perayaan/Acara", hex: "#FED7AA" },
  { key: "red", label: "Cuti Umum", hex: "#FECACA" },
  { key: "purple", label: "Lain-lain", hex: "#E9D5FF" },
];
const hexOf = (c) => (COLORS.find((x) => x.key === c) || COLORS[0]).hex;
const daysInMonth = (year, month) => new Date(year, month, 0).getDate();
const YEARS = Array.from({ length: 12 }, (_, i) => 2026 + i);

export default function SchoolCalendar() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.staff_role === "Hostel Supervisor";
  const [year, setYear] = useState(2026);
  const [entries, setEntries] = useState({});
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState(null);
  const exportRef = useRef(null);

  const load = async (y) => {
    try {
      const res = await api.get(`/school-calendar?year=${y}`);
      setEntries(res.data.entries || {});
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load calendar");
    }
  };
  useEffect(() => { load(year); setEditing(false); setDraft(null); setSelected(null); }, [year]);

  const source = editing ? draft : entries;
  const getCell = (key) => (source && source[key]) || { label: "", color: "none" };

  const startEdit = () => { setDraft({ ...entries }); setEditing(true); };
  const cancel = () => { setDraft(null); setEditing(false); setSelected(null); };

  const updateSelected = (patch) => {
    if (!selected) return;
    setDraft((d) => ({ ...d, [selected]: { ...getCell(selected), ...patch } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const clean = {};
      Object.entries(draft).forEach(([k, v]) => {
        if ((v.label && v.label.trim()) || (v.color && v.color !== "none")) clean[k] = { label: (v.label || "").trim(), color: v.color || "none" };
      });
      const res = await api.put("/school-calendar", { year, entries: clean });
      setEntries(res.data.entries || {});
      setDraft(null);
      setEditing(false);
      setSelected(null);
      toast.success("Takwim disimpan");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save calendar");
    } finally {
      setSaving(false);
    }
  };

  const exportCalendar = async () => {
    setExporting(true);
    try {
      await downloadElementAsPdf(exportRef.current, {
        filename: `school-calendar-${year}.pdf`,
        title: `School Calendar ${year}`,
      });
      toast.success("Takwim PDF downloaded");
    } catch (error) {
      toast.error("Unable to export the school calendar PDF");
    } finally {
      setExporting(false);
    }
  };

  const selCell = selected ? getCell(selected) : null;
  const selInfo = selected ? (() => {
    const [m, d] = selected.split("-").map(Number);
    return `${d} ${MONTHS[m - 1]} ${year} (${DOW[new Date(year, m - 1, d).getDay()]})`;
  })() : null;

  return (
    <section id="school-calendar-export" ref={exportRef} className="space-y-4 border-t-4 border-fuchsia-500 pt-4" data-testid="school-calendar-section" aria-labelledby="school-calendar-title">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-primary">
            <CalendarDays className="h-4 w-4" /> School Calendar
          </p>
          <h2 id="school-calendar-title" className="mt-1 font-display text-xl font-bold">Takwim Asrama — SMK Mutiara Impian · {year}</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-pdf-exclude>
          <Button variant="outline" onClick={exportCalendar} disabled={exporting || editing} title={editing ? "Save or cancel edits before exporting" : "Export the selected year as PDF"} data-testid="calendar-export-pdf-btn"><Download className="h-4 w-4" /> {exporting ? "Menjana PDF…" : "Export PDF"}</Button>
          <label className="text-sm text-muted-foreground" htmlFor="calendar-year">Tahun</label>
          <select
            id="calendar-year"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm font-semibold"
            data-testid="calendar-year-select"
          >
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {canEdit && (editing ? (
            <>
              <Button variant="outline" onClick={cancel} data-testid="calendar-cancel-btn"><X className="h-4 w-4" /> Batal</Button>
              <Button onClick={save} disabled={saving} data-testid="calendar-save-btn"><Save className="h-4 w-4" /> {saving ? "Menyimpan…" : "Simpan"}</Button>
            </>
          ) : (
            <Button variant="outline" onClick={startEdit} data-testid="calendar-edit-btn"><Pencil className="h-4 w-4" /> Edit takwim</Button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs" aria-label="Calendar color legend">
        {COLORS.filter((color) => color.key !== "none").map((color) => (
          <span key={color.key} className="inline-flex items-center gap-1.5">
            <span className="h-3 w-3 border border-border" style={{ backgroundColor: color.hex }} aria-hidden="true" />
            {color.label}
          </span>
        ))}
      </div>

      {editing && (
        <div className="rounded-xl border border-border bg-muted/40 p-4" data-testid="calendar-editor" data-pdf-exclude>
          {selected ? (
            <div className="space-y-3">
              <p className="text-sm font-semibold">{selInfo}</p>
              <Input value={selCell.label} onChange={(e) => updateSelected({ label: e.target.value })} placeholder="Catatan (cth: BALIK, OUTING, CUTI…)" data-testid="calendar-label-input" />
              <div className="flex flex-wrap gap-2">
                {COLORS.map((c) => (
                  <button
                    key={c.key}
                    onClick={() => updateSelected({ color: c.key })}
                    className={`flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${selCell.color === c.key ? "border-primary ring-2 ring-primary" : "border-border"}`}
                    style={{ backgroundColor: c.hex }}
                    data-testid={`calendar-color-${c.key}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Klik mana-mana kotak hari di bawah untuk menyunting catatan dan warnanya.</p>
          )}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="border-collapse text-xs" data-testid="school-calendar-table">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 border border-border bg-[#1E293B] px-2 py-2 text-white">Hari</th>
              {MONTHS.map((m) => (
                <th key={m} className="border border-border bg-[#1E293B] px-2 py-2 text-center font-bold text-white" style={{ minWidth: 120 }}>{m}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
              <tr key={day}>
                <td className="sticky left-0 z-10 border border-border bg-[#0F172A] px-2 py-1 text-center font-bold text-white">{day}</td>
                {MONTHS.map((_, mi) => {
                  const month = mi + 1;
                  if (day > daysInMonth(year, month)) return <td key={month} aria-disabled="true" data-testid={`calendar-cell-${month}-${day}`} className="border border-border bg-slate-100" />;
                  const key = `${month}-${day}`;
                  const cell = getCell(key);
                  const dow = new Date(year, mi, day).getDay();
                  const weekend = dow === 0 || dow === 6;
                  const isSel = editing && selected === key;
                  return (
                    <td
                      key={month}
                      onClick={editing ? () => setSelected(key) : undefined}
                      className={`border border-border px-1 py-1 align-top ${editing ? "cursor-pointer hover:outline hover:outline-1 hover:outline-primary" : ""} ${isSel ? "outline outline-2 outline-primary" : ""}`}
                      style={{ backgroundColor: hexOf(cell.color) }}
                      data-testid={`calendar-cell-${key}`}
                    >
                      <span className={`mr-1 text-[10px] font-semibold ${weekend ? "text-red-600" : "text-muted-foreground"}`}>{DOW[dow].slice(0, 2)}</span>
                      <span className="text-[11px] font-medium leading-tight">{cell.label}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEdit && !editing && (
        <p className="text-xs text-muted-foreground" data-pdf-exclude>Pilih tahun (2026 ke atas) dan klik &quot;Edit takwim&quot; untuk menyunting catatan serta mewarnakan setiap kotak hari.</p>
      )}
    </section>
  );
}
